"use client";

import { Checkbox } from "@/components/ui/checkbox";
import type { ColumnDef } from "@tanstack/react-table";
import { DataTableRowActions } from "./government-documents.actions";
import { DataTableColumnHeader } from "./government-documents.header";

type OfficialDocument = {
  id: string;
  documentType: string;
  number: string | null;
  title: string;
  description: string;
  date: Date;
  status: string;
  category: string | null;
  documentMediaId: string | null;
  organizationId: string;
  createdAt: Date;
  updatedAt: Date;
  // Extended fields
  deadlineDate: Date | null;
  awardedTo: string | null;
  estimatedValue: string | null;
  fiscalYear: number | null;
  codeSection: string | null;
  effectiveDate: Date | null;
};

// biome-ignore lint/correctness/noUnusedVariables: <explanation>
const typeLabels: Record<string, string> = {
  rfq: "RFQ",
  audit: "Audit",
  budget: "Budget",
  code: "Code",
  resolution: "Resolution",
};

const statusStyles: Record<string, string> = {
  current: "bg-green-100 text-green-800",
  archived: "bg-gray-100 text-gray-800",
  proposed: "bg-yellow-100 text-yellow-800",
  open: "bg-blue-100 text-blue-800",
  closed: "bg-gray-100 text-gray-800",
  awarded: "bg-purple-100 text-purple-800",
};

export const columns: ColumnDef<OfficialDocument>[] = [
  {
    id: "select",
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() ||
          (table.getIsSomePageRowsSelected() && "indeterminate")
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
        className="translate-y-[2px]"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
        className="translate-y-[2px]"
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: "number",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Number" />
    ),
    cell: ({ row }) => {
      const number = row.original.number;
      return (
        <div className="flex space-x-2">
          <span className="max-w-[100px] truncate font-medium">
            {number || "—"}
          </span>
        </div>
      );
    },
  },
  {
    accessorKey: "title",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Title" />
    ),
    cell: ({ row }) => (
      <div className="max-w-[300px]">{row.getValue("title")}</div>
    ),
    enableSorting: true,
    enableHiding: true,
    filterFn: (row, _id, value) => {
      return row.original.title.toLowerCase().includes(value.toLowerCase());
    },
  },
  {
    accessorKey: "date",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Date" />
    ),
    cell: ({ row }) => {
      const date = row.getValue("date") as Date;
      return <div>{new Date(date).toLocaleDateString()}</div>;
    },
    enableSorting: true,
  },
  {
    accessorKey: "fiscalYear",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Fiscal Year" />
    ),
    cell: ({ row }) => {
      const fiscalYear = row.original.fiscalYear;
      return <div>{fiscalYear || "—"}</div>;
    },
    enableSorting: true,
  },
  {
    accessorKey: "status",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status" />
    ),
    cell: ({ row }) => {
      const status = row.getValue("status") as string;
      return (
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${statusStyles[status] || "bg-gray-100 text-gray-800"}`}
        >
          {status}
        </span>
      );
    },
    enableSorting: true,
  },
  {
    id: "actions",
    cell: ({ row }) => <DataTableRowActions row={row} />,
  },
];
